// ./src/components/features/VolunteerRoles/VolunteerRolesFilterBar/VolunteerRolesFilterBar.tsx
//
// Filter controls for the Volunteer Roles list: by status (active / disabled).
// Fully controlled — the page owns the value and maps it to a VolunteerRoleFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";

export type VolunteerRoleStatusFilter = "active" | "disabled";

export interface VolunteerRolesFilterValue {
  status: VolunteerRoleStatusFilter | null;
}

export interface VolunteerRolesFilterBarProps {
  value: VolunteerRolesFilterValue;
  onChange: (value: VolunteerRolesFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_VOLUNTEER_ROLES_FILTER: VolunteerRolesFilterValue = {
  status: null,
};

const isFilterActive = (value: VolunteerRolesFilterValue): boolean => Boolean(value.status);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };

const ANY = "any";

const VolunteerRolesFilterBarComponent: React.FC<VolunteerRolesFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["volunteerRoles", "common"]);

  const statusOptions = [
    { value: ANY, label: t("volunteerRoles:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("volunteerRoles:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("volunteerRoles:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("volunteerRoles:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as VolunteerRoleStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_VOLUNTEER_ROLES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const VolunteerRolesFilterBar = React.memo(VolunteerRolesFilterBarComponent);
